import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle2,
  Mail,
  Calendar,
  ArrowLeft,
  BookOpen,
  Sparkles,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';


const BookingSuccessPage = ({ onOpenBooking, onOpenLogin }) => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <Header onLogin={onOpenLogin} onCtaBook={onOpenBooking} />

      <main className="relative pt-32 pb-24 overflow-hidden">
        {/* Soft amber radial glow */}
        <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-amber-400/[0.07] blur-[120px] pointer-events-none" />

        <div className="relative max-w-[860px] mx-auto px-6 lg:px-10">
          {/* Confirmation badge */}
          <div className="flex flex-col items-center text-center">
            <div className="relative">
              <div className="absolute -inset-6 bg-amber-400/20 rounded-full blur-2xl" />
              <div className="relative w-24 h-24 rounded-full bg-amber-400/15 border border-amber-400/60 flex items-center justify-center">
                <CheckCircle2 size={48} className="text-amber-400" strokeWidth={1.6} />
              </div>
            </div>

            <p className="mt-8 text-amber-400 tracking-[0.32em] text-[12px] font-medium">
              BOOKING CONFIRMED
            </p>
            <h1 className="mt-4 font-serif text-white text-4xl sm:text-5xl lg:text-[64px] leading-[1.05] tracking-tight">
              Your lesson is <span className="text-amber-400 italic">booked</span>
            </h1>
            <p className="mt-6 text-white/70 text-lg leading-relaxed max-w-xl">
              Thank you for scheduling with Kango9 School. I&apos;m looking forward to our
              session — see you on the board very soon.
            </p>
          </div>

          {/* What happens next */}
          <div className="mt-16 rounded-2xl border border-amber-400/30 bg-gradient-to-br from-amber-400/[0.06] to-transparent p-8 lg:p-10">
            <div className="flex items-center gap-3 mb-6">
              <Sparkles size={16} className="text-amber-400" />
              <p className="text-amber-400 tracking-[0.32em] text-[11px] font-medium">
                WHAT HAPPENS NEXT
              </p>
            </div>

            <ul className="space-y-6">
              <li className="flex gap-4">
                <div className="shrink-0 w-10 h-10 rounded-full border border-amber-400/40 flex items-center justify-center">
                  <Mail size={16} className="text-amber-400" />
                </div>
                <div>
                  <h3 className="text-white font-medium text-[17px]">
                    Check your inbox
                  </h3>
                  <p className="mt-1 text-white/60 text-[14.5px] leading-relaxed">
                    Calendly just sent you a confirmation email with the meeting details
                    and a calendar invite. If you don&apos;t see it within a few minutes,
                    check your spam folder.
                  </p>
                </div>
              </li>

              <li className="flex gap-4">
                <div className="shrink-0 w-10 h-10 rounded-full border border-amber-400/40 flex items-center justify-center">
                  <Calendar size={16} className="text-amber-400" />
                </div>
                <div>
                  <h3 className="text-white font-medium text-[17px]">
                    Add it to your calendar
                  </h3>
                  <p className="mt-1 text-white/60 text-[14.5px] leading-relaxed">
                    Use the calendar invite to save the lesson to Google, Apple or
                    Outlook. You&apos;ll receive a reminder the day before so you never
                    miss it.
                  </p>
                </div>
              </li>

              <li className="flex gap-4">
                <div className="shrink-0 w-10 h-10 rounded-full border border-amber-400/40 flex items-center justify-center">
                  <BookOpen size={16} className="text-amber-400" />
                </div>
                <div>
                  <h3 className="text-white font-medium text-[17px]">
                    Prepare for the session
                  </h3>
                  <p className="mt-1 text-white/60 text-[14.5px] leading-relaxed">
                    If you have recent games you&apos;d like to review, send the SGF
                    files or game links to{' '}
                    <a
                      href="mailto:baduk@kango9school.com"
                      className="text-amber-400 hover:underline"
                    >
                      baduk@kango9school.com
                    </a>{' '}
                    so I can prepare beforehand.
                  </p>
                </div>
              </li>
            </ul>
          </div>

          {/* Actions */}
          <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-amber-400 text-[#0a0a0a] hover:bg-amber-300 transition-colors duration-300 font-medium"
            >
              <ArrowLeft size={16} />
              Back to homepage
            </Link>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-amber-400/50 text-amber-400 hover:bg-amber-400 hover:text-[#0a0a0a] transition-colors duration-300 font-medium"
            >
              Read the blog while you wait
            </Link>
          </div>

          {/* Footer note */}
          <p className="mt-12 text-center text-white/40 text-sm">
            Need to reschedule or cancel? Use the link in your Calendly confirmation
            email, or reach me directly at{' '}
            <a
              href="mailto:baduk@kango9school.com"
              className="text-amber-400 hover:underline"
            >
              baduk@kango9school.com
            </a>
            .
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default BookingSuccessPage;
